import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';

// Lightweight, dependency-free guided tour.
// Each step targets a real DOM element via a CSS selector (usually a
// `data-tour="…"` attribute). If the element isn't on the page (e.g. the user
// lacks permission to see the "New Mission" button), the step falls back to a
// centered card so the tour never breaks.

const PADDING = 8;
const CARD_WIDTH = 340;
const CARD_GAP = 14;

const VisionTour = ({ steps, isOpen, onClose, onNavigate }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null); // target rect, or null = centered step

  const step = steps[index];

  const measure = useCallback(() => {
    if (!step) return;
    if (!step.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    // Read the rect after the scroll settles.
    requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    });
  }, [step]);

  // On each step: switch to the step's tab (if any), then measure once the new
  // tab's content has had a moment to render.
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    if (step?.tab) onNavigate?.(step.tab);
    const delay = step?.tab ? 340 : 0;
    const id = setTimeout(measure, delay);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, index]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onChange = () => measure();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [isOpen, measure]);

  // Reset to first step each time the tour is opened.
  useEffect(() => { if (isOpen) setIndex(0); }, [isOpen]);

  // Keyboard navigation.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, index]);

  if (!isOpen || !step) return null;

  const isFirst = index === 0;
  const isLast = index === steps.length - 1;

  const next = () => { if (!isLast) setIndex(i => i + 1); else finish(); };
  const back = () => { if (!isFirst) setIndex(i => i - 1); };
  const finish = () => { onClose?.(); };

  // ── Compute the tooltip card position ───────────────────────────────────
  let cardStyle;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const MARGIN = 12;
  const cardW = Math.min(CARD_WIDTH, vw - 32);
  if (!rect) {
    cardStyle = {
      top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      width: cardW,
      maxHeight: vh - MARGIN * 2
    };
  } else {
    // Usable space on each side of the target, after the gap + a safety margin.
    const spaceBelow = vh - (rect.top + rect.height) - CARD_GAP - MARGIN;
    const spaceAbove = rect.top - CARD_GAP - MARGIN;
    // Place the card on whichever side has more room — that side's space caps
    // the card height so a tall step always keeps its buttons on screen
    // (the card scrolls internally via overflow-y:auto).
    const placeBelow = spaceBelow >= spaceAbove;
    const top = placeBelow ? rect.top + rect.height + CARD_GAP : rect.top - CARD_GAP;
    let left = rect.left + rect.width / 2;
    // Clamp horizontally so the card stays on screen.
    const half = cardW / 2;
    left = Math.max(half + MARGIN, Math.min(left, vw - half - MARGIN));
    cardStyle = {
      top,
      left,
      transform: `translate(-50%, ${placeBelow ? '0' : '-100%'})`,
      width: cardW,
      maxHeight: Math.max(160, placeBelow ? spaceBelow : spaceAbove)
    };
  }

  return (
    <div className="v26-tour">
      {/* Click-catcher: swallows clicks so the page isn't interacted with mid-tour. */}
      <div className="v26-tour-catcher" onClick={() => {}} />

      {/* Spotlight: a transparent hole with a huge box-shadow dims everything else. */}
      {rect ? (
        <div
          className="v26-tour-spotlight"
          style={{
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2
          }}
        />
      ) : (
        <div className="v26-tour-dim" />
      )}

      {/* Tooltip card */}
      <div className="v26-tour-card" style={cardStyle}>
        {step.emoji && <div className="v26-tour-emoji">{step.emoji}</div>}
        <div className="v26-tour-step-count">Step {index + 1} of {steps.length}</div>
        <h3 className="v26-tour-title">{step.title}</h3>
        <div className="v26-tour-body">{step.body}</div>

        {step.example && (
          <div className="v26-tour-example">
            <div className="v26-tour-example-label">Example</div>
            {step.example}
          </div>
        )}

        <div className="v26-tour-dots">
          {steps.map((_, i) => (
            <span key={i} className={`v26-tour-dot ${i === index ? 'v26-tour-dot--active' : ''}`} />
          ))}
        </div>

        <div className="v26-tour-actions">
          <button className="v26-tour-skip" onClick={finish}>Skip</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isFirst && (
              <button className="v26-btn v26-btn--secondary v26-btn--sm" onClick={back}>Back</button>
            )}
            <button className="v26-btn v26-btn--primary v26-btn--sm" onClick={next}>
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisionTour;
